// Zebra Expr — parser. Tokens -> AST.
//
// Precedence (low to high):
//   WITH / IF  →  OR  →  AND  →  NOT  →  comparison  →  +/-  →  */%  →
//   unary(-)  →  property access (.)  →  primary
//
// AST node kinds: num, str, bool, null, field, prop, unary, binary, if, with,
// call, agg.

import { tokenize, ExprError } from './lexer.js';

const KEYWORDS = new Set(['IF', 'ELSE', 'WITH', 'AND', 'OR', 'NOT']);
// PARENT{…} is navigation rather than a reduction but shares the aggregate
// syntax; PERCENTILE#p / QUARTILE1 / QUARTILE3 are Structure's rank aggregates.
const AGG_NAMES = new Set(['SUM', 'COUNT', 'AVG', 'MIN', 'MAX', 'JOIN', 'MEDIAN', 'VALUES', 'ARRAY',
  'PARENT', 'PERCENTILE', 'QUARTILE1', 'QUARTILE3']);
const SCOPE_MODS = new Set(['children', 'subtree', 'leaves', 'all', 'ancestors', 'strict', 'descendants']);

export function parse(src) {
  const toks = tokenize(src);
  let p = 0;

  const peek = () => toks[p];
  const next = () => toks[p++];
  const isKw = (t, kw) => t.type === 'IDENT' && t.value.toUpperCase() === kw;
  const atOp = (v) => peek().type === 'OP' && peek().value === v;

  function expectOp(v) {
    const t = peek();
    if (t.type === 'OP' && t.value === v) { p++; return t; }
    throw new ExprError(`Expected "${v}"`, t.pos);
  }

  // An explicit lambda: `x -> body` or `(a, b) -> body`. Returns null (and
  // leaves the cursor untouched) when the tokens ahead are not one.
  function tryParseLambda() {
    const t = peek();
    const nx = toks[p + 1];
    if (t.type === 'IDENT' && !KEYWORDS.has(t.value.toUpperCase()) && nx && nx.type === 'OP' && nx.value === '->') {
      next(); next();
      return { kind: 'lambda', params: [t.value], body: parseExpr() };
    }
    if (t.type === 'OP' && t.value === '(') {
      // Look ahead for `( IDENT [, IDENT]* ) ->` without consuming anything.
      let q = p + 1;
      const params = [];
      if (toks[q] && toks[q].type === 'OP' && toks[q].value === ')') q++; // ()
      else {
        for (;;) {
          const id = toks[q];
          if (!id || id.type !== 'IDENT' || KEYWORDS.has(id.value.toUpperCase())) return null;
          params.push(id.value);
          q++;
          const sep = toks[q];
          if (sep && sep.type === 'OP' && (sep.value === ',' || sep.value === ';')) { q++; continue; }
          if (sep && sep.type === 'OP' && sep.value === ')') { q++; break; }
          return null;
        }
      }
      const arrow = toks[q];
      if (!arrow || arrow.type !== 'OP' || arrow.value !== '->') return null;
      p = q + 1;
      return { kind: 'lambda', params, body: parseExpr() };
    }
    return null;
  }

  function parseExpr() {
    const lambda = tryParseLambda();
    if (lambda) return lambda;
    const t = peek();
    const nx = toks[p + 1];
    const followedByParen = nx && nx.type === 'OP' && nx.value === '(';
    // `IF(` is the multi-clause function form; `IF cond :` is the construct.
    if (isKw(t, 'IF') && !followedByParen) return parseIf();
    // Structure also writes the construct with a parenthesized condition:
    // `IF (cond) : value [ELSE : value]`. Try that shape and rewind to the
    // plain call form when the colon never appears (e.g. `IF(a, b)`).
    if (isKw(t, 'IF') && followedByParen) {
      const start = p;
      try {
        next(); next(); // IF (
        const cond = parseExpr();
        expectOp(')');
        if (atOp(':')) {
          next();
          const then = parseExpr();
          if (isKw(peek(), 'ELSE')) { next(); expectOp(':'); return { kind: 'if', cond, then, else: parseExpr() }; }
          return { kind: 'if', cond, then, else: null };
        }
      } catch (_e) { /* not the colon form — reparse as a call */ }
      p = start;
    }
    if (isKw(t, 'WITH')) return parseWith();
    return parseOr();
  }

  function parseIf() {
    next(); // IF
    const cond = parseOr();
    expectOp(':');
    const then = parseExpr();
    const e = peek();
    // ELSE is optional — `IF cond : value` (no else) yields empty when false,
    // matching the call-form `IF(cond, value)`. Lets a bare colon-IF sit in an
    // `or` chain (its falsy result falls through to the next branch).
    if (!isKw(e, 'ELSE')) return { kind: 'if', cond, then, else: null };
    next(); // ELSE
    expectOp(':');
    const els = parseExpr();
    return { kind: 'if', cond, then, else: els };
  }

  function parseWith() {
    next(); // WITH
    const nameTok = peek();
    if (nameTok.type !== 'IDENT') throw new ExprError('Expected variable name after WITH', nameTok.pos);
    next();
    // Function form: WITH name(p1, p2, ...) = expr : body
    if (atOp('(')) {
      next();
      const params = [];
      if (!atOp(')')) {
        const first = peek();
        if (first.type !== 'IDENT') throw new ExprError('Expected parameter name', first.pos);
        params.push(next().value);
        while (atOp(',') || atOp(';')) {
          next();
          const p = peek();
          if (p.type !== 'IDENT') throw new ExprError('Expected parameter name', p.pos);
          params.push(next().value);
        }
      }
      expectOp(')');
      expectOp('=');
      const value = parseOr();
      expectOp(':');
      const body = parseExpr();
      return { kind: 'withfn', name: nameTok.value, params, value, body };
    }
    // Variable form: WITH name = expr : body
    expectOp('=');
    const value = parseOr();
    expectOp(':');
    const body = parseExpr();
    return { kind: 'with', name: nameTok.value, value, body };
  }

  function parseOr() {
    let left = parseAnd();
    while (isKw(peek(), 'OR')) { next(); left = { kind: 'binary', op: 'OR', left, right: parseAnd() }; }
    return left;
  }
  function parseAnd() {
    let left = parseNot();
    while (isKw(peek(), 'AND')) { next(); left = { kind: 'binary', op: 'AND', left, right: parseNot() }; }
    return left;
  }
  function parseNot() {
    if (isKw(peek(), 'NOT')) { next(); return { kind: 'unary', op: 'NOT', operand: parseNot() }; }
    return parseComparison();
  }
  function parseComparison() {
    let left = parseConcat();
    const t = peek();
    if (t.type === 'OP' && ['=', '!=', '<', '<=', '>', '>='].includes(t.value)) {
      next();
      left = { kind: 'binary', op: t.value, left, right: parseConcat() };
    }
    return left;
  }
  // Structure's infix string concatenation: `a CONCAT b CONCAT c`. Sits
  // between comparison and additive, so `a CONCAT b = c` compares the joined
  // string. `CONCAT(a, b)` at expression start still parses as the call form.
  function parseConcat() {
    let left = parseAdd();
    while (peek().type === 'IDENT' && /^concat$/i.test(peek().value)) {
      next();
      left = { kind: 'binary', op: 'CONCAT', left, right: parseAdd() };
    }
    return left;
  }
  function parseAdd() {
    let left = parseMul();
    while (atOp('+') || atOp('-')) { const op = next().value; left = { kind: 'binary', op, left, right: parseMul() }; }
    return left;
  }
  function parseMul() {
    let left = parseUnary();
    while (atOp('*') || atOp('/') || atOp('%')) { const op = next().value; left = { kind: 'binary', op, left, right: parseUnary() }; }
    return left;
  }
  function parseUnary() {
    if (atOp('-')) { next(); return { kind: 'unary', op: '-', operand: parseUnary() }; }
    return parsePostfix();
  }
  function parsePostfix() {
    let node = parsePrimary();
    while (atOp('.')) {
      next();
      const nameTok = peek();
      if (nameTok.type !== 'IDENT') throw new ExprError('Expected property name after "."', nameTok.pos);
      next();
      // Method-call sugar: `x.NAME(args)` desugars to the function call
      // `NAME(x, ...args)` so Jira Structure-style chains work as-is, e.g.
      // `assemblyInternalDeadline.FORMAT_DATETIME("yyyy-MM-dd")`.
      if (atOp('(')) {
        next();
        const args = [node];
        if (!atOp(')')) {
          args.push(parseExpr());
          while (atOp(',') || atOp(';')) { next(); args.push(parseExpr()); }
        }
        expectOp(')');
        node = { kind: 'call', name: nameTok.value.toUpperCase(), args };
      } else {
        node = { kind: 'prop', object: node, name: nameTok.value };
      }
    }
    return node;
  }

  function parsePrimary() {
    const t = peek();

    if (t.type === 'NUMBER') { next(); return { kind: 'num', value: t.value }; }
    if (t.type === 'STRING') { next(); return { kind: 'str', value: t.value }; }

    // Triple-quoted template: concat of literal parts and sub-parsed ${expr}s.
    if (t.type === 'TEMPLATE') {
      next();
      return {
        kind: 'template',
        parts: t.parts.map((p) => (p.t === 'str' ? { kind: 'str', value: p.v } : parse(p.src))),
      };
    }

    if (atOp('(')) { next(); const e = parseExpr(); expectOp(')'); return e; }

    if (t.type === 'IDENT') {
      const upper = t.value.toUpperCase();
      if (upper === 'TRUE') { next(); return { kind: 'bool', value: true }; }
      if (upper === 'FALSE') { next(); return { kind: 'bool', value: false }; }
      if (upper === 'NULL') { next(); return { kind: 'null' }; }

      next(); // consume ident
      // Aggregate: NAME [#mods] { expr }   (or NAME { expr })
      if (peek().type === 'MOD' || atOp('{')) {
        const scope = { type: null, extra: {} };
        while (peek().type === 'MOD') {
          const m = next();
          const mn = m.value.toLowerCase();
          if (SCOPE_MODS.has(mn)) scope.type = mn === 'descendants' ? 'subtree' : mn;
          else scope.extra[mn] = m.modValue !== undefined ? m.modValue : true;
        }
        expectOp('{');
        const body = parseExpr();
        expectOp('}');
        return { kind: 'agg', name: upper, scope, body };
      }
      // Function call: NAME( args )   — args separated by `,` or `;`
      if (atOp('(')) {
        next();
        const args = [];
        if (!atOp(')')) {
          args.push(parseExpr());
          while (atOp(',') || atOp(';')) { next(); args.push(parseExpr()); }
        }
        expectOp(')');
        return { kind: 'call', name: upper, args };
      }
      // A bare keyword that wasn't a call/aggregate is a syntax error.
      if (KEYWORDS.has(upper)) throw new ExprError(`Unexpected keyword "${t.value}"`, t.pos);
      // Otherwise: a field reference.
      return { kind: 'field', name: t.value };
    }

    throw new ExprError(`Unexpected token${t.value != null ? ` "${t.value}"` : ''}`, t.pos);
  }

  const ast = parseExpr();
  if (peek().type !== 'EOF') throw new ExprError(`Unexpected trailing input`, peek().pos);
  return ast;
}

export { AGG_NAMES };
